import React, { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import {
  Eye,
  EyeOff,
  AlertCircle,
  CheckCircle,
  Check,
  User,
  Mail,
  Lock,
  Building2,
  Stethoscope,
  MessageSquare,
  ChevronDown,
  ArrowRight,
  ArrowLeft,
  Loader2
} from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import AuthShell from '../../components/auth/AuthShell'
import toast from 'react-hot-toast'

// Multi-step wizard — 3 natural field groups so each step fits the viewport
// without scrolling. One react-hook-form instance backs all three steps;
// shouldUnregister defaults to false, so stepping Back never loses input.
const STEPS = [
  { label: 'Who you are', heading: 'Who you are', description: "Let's start with the basics." },
  { label: 'Where you work', heading: 'Where you work', description: 'Tell us about your organization.' },
  { label: 'Secure your account', heading: 'Secure your account', description: 'Choose a strong password.' },
]
const TOTAL_STEPS = STEPS.length
const STEP_FIELDS = {
  1: ['firstName', 'lastName', 'email'],
  2: ['organization', 'specialization', 'accessReason'],
  3: ['password', 'confirmPassword'],
}

function StepIndicator({ step }) {
  return (
    <div className="mb-7">
      <p className="sr-only" role="status" aria-live="polite">
        {`Step ${step} of ${TOTAL_STEPS}: ${STEPS[step - 1].label}`}
      </p>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-gray-500">
          Step {step} of {TOTAL_STEPS}
        </span>
        <span className="text-xs text-gray-400">{STEPS[step - 1].label}</span>
      </div>
      <ol className="flex items-center" aria-hidden="true">
        {STEPS.map((s, i) => {
          const n = i + 1
          const state = n < step ? 'done' : n === step ? 'current' : 'upcoming'
          return (
            <li key={s.label} className="flex-1 flex items-center last:flex-none">
              <span
                className={
                  'flex items-center justify-center w-6 h-6 rounded-full text-[11px] font-semibold shrink-0 transition-colors duration-200 ' +
                  (state === 'done'
                    ? 'bg-primary-600 text-white'
                    : state === 'current'
                    ? 'bg-white text-primary-600 ring-2 ring-primary-600'
                    : 'bg-gray-100 text-gray-400')
                }
              >
                {state === 'done' ? <Check className="w-3 h-3" strokeWidth={2.5} /> : n}
              </span>
              {n < TOTAL_STEPS && (
                <span
                  className={
                    'flex-1 h-px mx-2 transition-colors duration-200 ' +
                    (n < step ? 'bg-primary-600' : 'bg-gray-200')
                  }
                />
              )}
            </li>
          )
        })}
      </ol>
    </div>
  )
}

const RegisterPage = () => {
  const { register: registerUser } = useAuth()
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [registrationSuccess, setRegistrationSuccess] = useState(false)
  const [step, setStep] = useState(1)
  const reduceMotion = useReducedMotion()
  const headingRef = useRef(null)

  const {
    register,
    handleSubmit,
    trigger,
    formState: { errors },
    setError,
    watch
  } = useForm()

  const password = watch('password')

  // Move focus to the new step's heading on every transition, so keyboard
  // and screen-reader users land somewhere sensible. Focus fires from
  // `onAnimationComplete` on the incoming `motion.div`, not a `step` effect:
  // `<AnimatePresence mode="wait">` keeps the outgoing step mounted through
  // its exit animation, so a `useEffect([step])` would fire before the new
  // heading ref exists and silently no-op.
  const goNext = async () => {
    const valid = await trigger(STEP_FIELDS[step])
    if (!valid) return
    setStep((s) => Math.min(s + 1, TOTAL_STEPS))
  }
  const goBack = () => setStep((s) => Math.max(s - 1, 1))

  // Enter in any field before the last step advances instead of submitting.
  // Textarea is excluded (Enter inserts a newline there). Final step is a
  // no-op here, letting the native Enter → submit path run once.
  const handleFormKeyDown = (e) => {
    if (e.key !== 'Enter') return
    if (e.target?.tagName === 'TEXTAREA') return
    if (step < TOTAL_STEPS) {
      e.preventDefault()
      goNext()
    }
  }

  const onSubmit = async (data) => {
    setIsLoading(true)
    try {
      const result = await registerUser({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
        organization: data.organization,
        specialization: data.specialization,
        accessReason: data.accessReason
      })

      if (result.success) {
        setRegistrationSuccess(true)
        toast.success('Registration successful! Please wait for account approval.')
      } else {
        setError('root', {
          type: 'manual',
          message: result.error || 'Registration failed'
        })
      }
    } catch (error) {
      console.error('Registration error:', error)
      setError('root', {
        type: 'manual',
        message: 'An unexpected error occurred. Please try again.'
      })
    } finally {
      setIsLoading(false)
    }
  }

  if (registrationSuccess) {
    return (
      <AuthShell>
        <motion.div
          initial={reduceMotion ? undefined : { opacity: 0, scale: 0.97 }}
          animate={reduceMotion ? undefined : { opacity: 1, scale: 1 }}
          transition={reduceMotion ? undefined : { duration: 0.35, ease: 'easeOut' }}
          className="text-center"
        >
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-success-100">
            <CheckCircle className="h-8 w-8 text-success-600" strokeWidth={1.75} />
          </div>
          <h2 className="mt-5 text-2xl font-bold tracking-tight text-gray-900">
            Registration submitted
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Your account registration has been submitted successfully.
            An administrator will review your request and approve your access.
          </p>
          <p className="mt-3 text-sm text-gray-500">
            You will receive an email notification once your account is approved.
          </p>
          <Link
            to="/login"
            className="btn-brand btn-lg w-full mt-7"
          >
            Return to login
          </Link>
        </motion.div>
      </AuthShell>
    )
  }

  const slideVariants = {
    enter: (dir) => (reduceMotion ? { opacity: 0 } : { opacity: 0, x: dir > 0 ? 24 : -24 }),
    center: { opacity: 1, x: 0 },
    exit: (dir) => (reduceMotion ? { opacity: 0 } : { opacity: 0, x: dir > 0 ? -24 : 24 }),
  }

  return (
    <AuthShell>
      <div>
        <div className="mb-6 text-center">
          <h2 className="text-2xl font-bold tracking-tight text-gray-900">
            Create your account
          </h2>
          <p className="mt-1.5 text-sm text-gray-600">
            Join the Integrated Early Warning and Response System
          </p>
        </div>

        <StepIndicator step={step} />

        <form
          className="space-y-5"
          onSubmit={handleSubmit(onSubmit)}
          onKeyDown={handleFormKeyDown}
          noValidate
        >
          <AnimatePresence mode="wait" custom={1}>
            <motion.div
              key={step}
              custom={1}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.2, ease: 'easeOut' }}
              onAnimationComplete={() => headingRef.current?.focus()}
            >
              {/* Global error — only ever set from the final-step submit */}
              {errors.root && (
                <div className="alert-danger mb-5" role="alert">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertCircle className="h-5 w-5 text-danger-400" strokeWidth={1.75} />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm">{errors.root.message}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 1 — Who you are */}
              {step === 1 && (
                <div className="space-y-5">
                  <h3 ref={headingRef} tabIndex={-1} className="text-base font-semibold text-gray-900 outline-none">
                    {STEPS[0].heading}
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="firstName" className="field-label">
                        First name
                      </label>
                      <div className="mt-2 relative">
                        <User className="input-icon" strokeWidth={1.75} />
                        <input
                          {...register('firstName', {
                            required: 'First name is required',
                            minLength: { value: 2, message: 'First name must be at least 2 characters' }
                          })}
                          id="firstName"
                          type="text"
                          autoComplete="given-name"
                          disabled={isLoading}
                          aria-invalid={errors.firstName ? 'true' : 'false'}
                          aria-describedby={errors.firstName ? 'firstName-error' : undefined}
                          className={`input pl-11 ${errors.firstName ? 'input-error' : ''}`}
                          placeholder="John"
                        />
                      </div>
                      {errors.firstName && (
                        <p id="firstName-error" className="mt-1.5 text-xs text-danger-600">{errors.firstName.message}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="lastName" className="field-label">
                        Last name
                      </label>
                      <div className="mt-2 relative">
                        <User className="input-icon" strokeWidth={1.75} />
                        <input
                          {...register('lastName', {
                            required: 'Last name is required',
                            minLength: { value: 2, message: 'Last name must be at least 2 characters' }
                          })}
                          id="lastName"
                          type="text"
                          autoComplete="family-name"
                          disabled={isLoading}
                          aria-invalid={errors.lastName ? 'true' : 'false'}
                          aria-describedby={errors.lastName ? 'lastName-error' : undefined}
                          className={`input pl-11 ${errors.lastName ? 'input-error' : ''}`}
                          placeholder="Doe"
                        />
                      </div>
                      {errors.lastName && (
                        <p id="lastName-error" className="mt-1.5 text-xs text-danger-600">{errors.lastName.message}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="field-label">
                      Email address
                    </label>
                    <div className="mt-2 relative">
                      <Mail className="input-icon" strokeWidth={1.75} />
                      <input
                        {...register('email', {
                          required: 'Email is required',
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'Invalid email address'
                          }
                        })}
                        id="email"
                        type="email"
                        autoComplete="email"
                        disabled={isLoading}
                        aria-invalid={errors.email ? 'true' : 'false'}
                        aria-describedby={errors.email ? 'email-error' : undefined}
                        className={`input pl-11 ${errors.email ? 'input-error' : ''}`}
                        placeholder="you@example.com"
                      />
                    </div>
                    {errors.email && (
                      <p id="email-error" className="mt-1.5 text-sm text-danger-600">{errors.email.message}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Step 2 — Where you work */}
              {step === 2 && (
                <div className="space-y-5">
                  <h3 ref={headingRef} tabIndex={-1} className="text-base font-semibold text-gray-900 outline-none">
                    {STEPS[1].heading}
                  </h3>

                  <div>
                    <label htmlFor="organization" className="field-label">
                      Organization
                    </label>
                    <div className="mt-2 relative">
                      <Building2 className="input-icon" strokeWidth={1.75} />
                      <input
                        {...register('organization', { required: 'Organization is required' })}
                        id="organization"
                        type="text"
                        disabled={isLoading}
                        aria-invalid={errors.organization ? 'true' : 'false'}
                        aria-describedby={errors.organization ? 'organization-error' : undefined}
                        className={`input pl-11 ${errors.organization ? 'input-error' : ''}`}
                        placeholder="e.g. Ministry of Health"
                      />
                    </div>
                    {errors.organization && (
                      <p id="organization-error" className="mt-1.5 text-sm text-danger-600">{errors.organization.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="specialization" className="field-label">
                      Specialization
                    </label>
                    <div className="mt-2 relative">
                      <Stethoscope className="input-icon" strokeWidth={1.75} />
                      <select
                        {...register('specialization', { required: 'Specialization is required' })}
                        id="specialization"
                        disabled={isLoading}
                        aria-invalid={errors.specialization ? 'true' : 'false'}
                        aria-describedby={errors.specialization ? 'specialization-error' : undefined}
                        className={`input select-arrow pl-11 ${errors.specialization ? 'input-error' : ''}`}
                      >
                        <option value="">Select your specialization</option>
                        <option value="cardiology">Cardiology</option>
                        <option value="neurology">Neurology</option>
                        <option value="oncology">Oncology</option>
                        <option value="pediatrics">Pediatrics</option>
                        <option value="emergency_medicine">Emergency Medicine</option>
                        <option value="data_science">Data Science</option>
                        <option value="machine_learning">Machine Learning</option>
                        <option value="health_informatics">Health Informatics</option>
                        <option value="research">Medical Research</option>
                        <option value="other">Other</option>
                      </select>
                      <ChevronDown className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" strokeWidth={1.75} />
                    </div>
                    {errors.specialization && (
                      <p id="specialization-error" className="mt-1.5 text-sm text-danger-600">{errors.specialization.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="accessReason" className="field-label">
                      Reason for access
                    </label>
                    <div className="mt-2 relative">
                      <MessageSquare className="input-icon-top" strokeWidth={1.75} />
                      <textarea
                        {...register('accessReason', {
                          required: 'Please explain why you need access',
                          minLength: { value: 50, message: 'Please provide at least 50 characters explanation' }
                        })}
                        id="accessReason"
                        rows={3}
                        disabled={isLoading}
                        aria-invalid={errors.accessReason ? 'true' : 'false'}
                        aria-describedby={errors.accessReason ? 'accessReason-error' : undefined}
                        className={`input pl-11 resize-y ${errors.accessReason ? 'input-error' : ''}`}
                        placeholder="Describe your intended use of the IEWRS system for research or clinical purposes..."
                      />
                    </div>
                    {errors.accessReason && (
                      <p id="accessReason-error" className="mt-1.5 text-sm text-danger-600">{errors.accessReason.message}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Step 3 — Secure your account */}
              {step === 3 && (
                <div className="space-y-5">
                  <h3 ref={headingRef} tabIndex={-1} className="text-base font-semibold text-gray-900 outline-none">
                    {STEPS[2].heading}
                  </h3>

                  <div>
                    <label htmlFor="password" className="field-label">
                      Password
                    </label>
                    <div className="mt-2 relative">
                      <Lock className="input-icon" strokeWidth={1.75} />
                      <input
                        {...register('password', {
                          required: 'Password is required',
                          minLength: { value: 8, message: 'Password must be at least 8 characters' },
                          pattern: {
                            value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                            message: 'Password must contain uppercase, lowercase, and number'
                          }
                        })}
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        disabled={isLoading}
                        aria-invalid={errors.password ? 'true' : 'false'}
                        aria-describedby={errors.password ? 'password-error' : undefined}
                        className={`input pl-11 pr-11 ${errors.password ? 'input-error' : ''}`}
                        placeholder="At least 8 characters"
                      />
                      <button
                        type="button"
                        className="input-trailing-btn"
                        onClick={() => setShowPassword(!showPassword)}
                        disabled={isLoading}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" strokeWidth={1.75} /> : <Eye className="h-4 w-4" strokeWidth={1.75} />}
                      </button>
                    </div>
                    {errors.password && (
                      <p id="password-error" className="mt-1.5 text-sm text-danger-600">{errors.password.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="field-label">
                      Confirm password
                    </label>
                    <div className="mt-2 relative">
                      <Lock className="input-icon" strokeWidth={1.75} />
                      <input
                        {...register('confirmPassword', {
                          required: 'Please confirm your password',
                          validate: value => value === password || 'Passwords do not match'
                        })}
                        id="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        disabled={isLoading}
                        aria-invalid={errors.confirmPassword ? 'true' : 'false'}
                        aria-describedby={errors.confirmPassword ? 'confirmPassword-error' : undefined}
                        className={`input pl-11 pr-11 ${errors.confirmPassword ? 'input-error' : ''}`}
                        placeholder="Repeat password"
                      />
                      <button
                        type="button"
                        className="input-trailing-btn"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        disabled={isLoading}
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      >
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" strokeWidth={1.75} /> : <Eye className="h-4 w-4" strokeWidth={1.75} />}
                      </button>
                    </div>
                    {errors.confirmPassword && (
                      <p id="confirmPassword-error" className="mt-1.5 text-sm text-danger-600">{errors.confirmPassword.message}</p>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Step navigation */}
          <div className="flex gap-3 pt-1">
            {step > 1 && (
              <button
                type="button"
                onClick={goBack}
                disabled={isLoading}
                className="btn-secondary btn-lg gap-2 px-5"
              >
                <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
                Back
              </button>
            )}

            {step < TOTAL_STEPS && (
              <button
                type="button"
                onClick={goNext}
                className="btn-brand btn-lg flex-1 gap-2"
              >
                Continue
                <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
              </button>
            )}

            {step === TOTAL_STEPS && (
              <button
                type="submit"
                disabled={isLoading}
                className="btn-brand btn-lg flex-1 gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.75} />
                    Creating account&hellip;
                  </>
                ) : (
                  <>
                    Create account
                    <ArrowRight className="h-4 w-4" strokeWidth={1.75} />
                  </>
                )}
              </button>
            )}
          </div>
        </form>

        {/* Login link */}
        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Already have an account?</span>
            </div>
          </div>

          <div className="mt-5">
            <Link
              to="/login"
              className="btn-secondary btn-lg w-full"
            >
              Sign in instead
            </Link>
          </div>
        </div>
      </div>

      {/* Note about approval */}
      <p className="mt-6 text-center text-xs text-gray-500">
        All accounts require administrator approval for security purposes.
        You will be notified via email once your account is approved.
      </p>
    </AuthShell>
  )
}

export default RegisterPage
